import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole, getRoleDisplayName, getRoleBadgeColor } from '../../lib/roleUtils';

interface TeamMember {
  id: string;
  email: string;
  full_name?: string;
  role?: string;
  created_at: string;
  is_active: boolean;
}

const TeamManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showConfirmRemove, setShowConfirmRemove] = useState<{ show: boolean; memberId: string | null; memberName: string }>({
    show: false,
    memberId: null,
    memberName: ''
  });
  const [newMember, setNewMember] = useState({
    email: '',
    full_name: '',
    role: 'viewer' as const
  });
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    setLoading(true);
    try {
      console.log('Fetching team members...');
      
      // Only fetch users who are actual team members (have a role assigned)
      // and exclude subscription customers
      const { data, error } = await supabase
        .from('users')
        .select(`
          id, 
          email, 
          full_name, 
          role, 
          created_at
        `)
        .not('role', 'is', null) // Only users with a role assigned
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching team members:', error);
        setToast({ type: 'error', message: 'Failed to fetch team members' });
      } else {
        console.log('Raw team members data:', data);
        const members = data?.map((member: any) => ({
          ...member,
          role: member.role || 'viewer', // Default to viewer if no role
          is_active: true
        })) || [];
        console.log('Processed team members:', members);
        setTeamMembers(members);
      }
    } catch (err) {
      console.error('Exception fetching team members:', err);
      setToast({ type: 'error', message: 'An unexpected error occurred' });
    }
    setLoading(false);
  };

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newMember.email || !newMember.full_name) {
      setToast({ type: 'error', message: 'Please fill in all required fields' });
      return;
    }

    try {
      // First, check if user already exists
      const { data: existingUsers, error: userCheckError } = await supabase
        .from('users')
        .select('id, email, role')
        .eq('email', newMember.email);
      
      if (userCheckError) {
        throw userCheckError;
      }

      // Check if we have exactly one user or multiple users
      if (existingUsers && existingUsers.length > 0) {
        if (existingUsers.length > 1) {
          // Multiple users with same email - this shouldn't happen but handle it gracefully
          console.warn('Multiple users found with same email:', existingUsers);
          setToast({ type: 'error', message: 'Multiple users found with this email. Please contact support.' });
          return;
        }
        
        // Exactly one user exists, update their role
        const existingUser = existingUsers[0];
        const { error: updateError } = await supabase
          .from('users')
          .update({ 
            role: newMember.role,
            full_name: newMember.full_name
          })
          .eq('id', existingUser.id);

        if (updateError) {
          throw updateError;
        }
        
        setToast({ type: 'success', message: 'Team member role updated successfully!' });
      } else {
        // No user exists, we cannot pre-create users without valid auth.users entries
        // Instead, we'll store this as a pending invitation that can be processed later
        // For now, we'll show a message about how to handle this
        
        setToast({ 
          type: 'error', 
          message: 'Cannot add team members who haven\'t signed up yet. Please ask them to sign up first, then add them here.' 
        });
        return;
      }

      // Reset form and refresh team members
      setNewMember({ email: '', full_name: '', role: 'viewer' });
      setShowAddMember(false);
      
      // Show success message and redirect to admin page
      setToast({ type: 'success', message: 'Team member added successfully! Redirecting to admin dashboard...' });
      
      // Redirect after a short delay to show the success message
      setTimeout(() => {
        navigate('/admin');
      }, 1500);
      
    } catch (error: any) {
      console.error('Error adding team member:', error);
      setToast({ type: 'error', message: `Failed to add team member: ${error.message}` });
    }
  };

  const handleRoleChange = async (memberId: string, newRole: UserRole) => {
    try {
      // Check if user has a role first
      const { data: currentUser, error: fetchError } = await supabase
        .from('users')
        .select('role')
        .eq('id', memberId)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      let result;
      if (currentUser.role) {
        // Update existing role
        result = await supabase
          .from('users')
          .update({ role: newRole })
          .eq('id', memberId);
      } else {
        // Insert role if it doesn't exist
        result = await supabase
          .from('users')
          .update({ role: newRole })
          .eq('id', memberId);
      }

      if (result.error) {
        throw result.error;
      }

      setToast({ type: 'success', message: 'Role updated successfully! Redirecting to admin dashboard...' });
      
      // Redirect after a short delay to show the success message
      setTimeout(() => {
        navigate('/admin');
      }, 1500);
    } catch (error: any) {
      console.error('Error updating role:', error);
      setToast({ type: 'error', message: `Failed to update role: ${error.message}` });
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      console.log('Removing team member with ID:', memberId);
      
      // Instead of deleting the user, remove their role to make them no longer a team member
      const { error } = await supabase
        .from('users')
        .update({ role: null })
        .eq('id', memberId);

      if (error) {
        console.error('Supabase error when removing team member:', error);
        throw error;
      }

      console.log('Successfully removed role from user:', memberId);
      setToast({ type: 'success', message: 'Team member removed successfully!' });
      // Refresh the team members list to reflect the change
      fetchTeamMembers();
      setShowConfirmRemove({ show: false, memberId: null, memberName: '' });
    } catch (error: any) {
      console.error('Error removing team member:', error);
      setToast({ type: 'error', message: `Failed to remove team member: ${error.message}` });
    }
  };

  const confirmRemoveMember = (memberId: string, memberName: string) => {
    setShowConfirmRemove({ show: true, memberId, memberName });
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading team members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {toast && (
        <div className={`mb-4 px-4 py-2 rounded text-white font-medium ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
          {toast.message}
        </div>
      )}

      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Management</h1>
          <p className="text-sm text-gray-600 mt-1">Manage your internal team members and their permissions. You'll be redirected to the admin dashboard after adding or updating team members.</p>
        </div>
        <button
          onClick={() => setShowAddMember(true)}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/80 transition-colors"
        >
          + Add Team Member
        </button>
      </div>

      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          💡 <strong>Team Members vs Customers:</strong> This page only shows internal team members who have been assigned roles. 
          Subscription customers are managed separately and won't appear here. You can only add team members who have already signed up to the system.
        </p>
        <p className="text-sm text-blue-800 mt-2">
          🔒 <strong>Security Note:</strong> New users automatically start as regular users without any admin access. 
          They only become team members when you explicitly add them here through the admin panel.
        </p>
      </div>

      {/* Customer Management Note */}
      <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-gray-800">Managing Subscription Customers</h3>
            <div className="mt-2 text-sm text-gray-700">
              <p>Subscription customers are managed through the subscription system, not team management. They can:</p>
              <ul className="mt-1 list-disc list-inside space-y-1">
                <li>Submit service requests based on their plan</li>
                <li>Access their dashboard and project files</li>
                <li>Communicate with your team through the request system</li>
              </ul>
              <p className="mt-2">To view all users (including customers), you can check the database directly or create a separate admin view.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Team Members List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Team Members ({teamMembers.length})</h2>
          <p className="text-sm text-gray-600 mb-4">Users with assigned roles who can access the admin system</p>
          
          {/* Summary Stats */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Team Members:</span>
              <span className="font-medium text-gray-900">{teamMembers.length}</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Note: This shows only users with assigned roles. Subscription customers are managed separately.
            </div>
          </div>

          <div className="overflow-x-auto">
            {teamMembers.length === 0 ? (
              <div className="text-center py-8">
                <div className="mx-auto h-12 w-12 text-gray-400">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No team members</h3>
                <p className="mt-1 text-sm text-gray-500">Get started by adding your first team member.</p>
                <div className="mt-6">
                  <button
                    onClick={() => setShowAddMember(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    + Add Team Member
                  </button>
                </div>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {teamMembers.map((member) => (
                    <tr key={member.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-primary font-semibold text-sm">
                                {(member.full_name || member.email).charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {member.full_name || member.email || 'No Name'}
                            </div>
                            <div className="text-sm text-gray-500">{member.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={member.role}
                          onChange={(e) => handleRoleChange(member.id, e.target.value as UserRole)}
                          className={`text-sm px-3 py-1 rounded-full font-medium ${getRoleBadgeColor(member.role as UserRole)} border-0`}
                          disabled={member.id === user?.id}
                        >
                          <option value="admin">Admin</option>
                          <option value="moderator">Moderator</option>
                          <option value="viewer">Viewer</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(member.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {member.id !== user?.id && (
                          <button
                            onClick={() => confirmRemoveMember(member.id, member.full_name || member.email)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddMember && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add Team Member</h3>
              
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-xs text-blue-700">
                  📝 <strong>Note:</strong> You can only add users who have already signed up to the system. 
                  If the person hasn't created an account yet, please ask them to sign up first.
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  ✅ After successfully adding a team member, you'll be redirected to the admin dashboard.
                </p>
              </div>
              
              <form onSubmit={handleAddMember}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={newMember.email}
                    onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="user@example.com"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={newMember.full_name}
                    onChange={(e) => setNewMember({ ...newMember, full_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role
                  </label>
                  <select
                    value={newMember.role}
                    onChange={(e) => setNewMember({ ...newMember, role: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="viewer">Viewer</option>
                    <option value="moderator">Moderator</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowAddMember(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/80 transition-colors"
                  >
                    Add Member
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Removing Members */}
      {showConfirmRemove.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Team Member Removal</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to remove <strong>{showConfirmRemove.memberName}</strong> from the team?
                This will remove their admin access but keep their account intact. They can still access the system as a regular user if they have an active subscription.
              </p>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowConfirmRemove({ show: false, memberId: null, memberName: '' })}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => showConfirmRemove.memberId && handleRemoveMember(showConfirmRemove.memberId)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Remove from Team
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagement; 